/**
 * On-device Web Speech API helpers for dictation (Chrome desktop 139+).
 * Never falls back to cloud Web Speech — callers should use Deepgram instead.
 */

export const CHROME_ON_DEVICE_LANGUAGES = [
  'de-DE',
  'en-US',
  'es-ES',
  'fr-FR',
  'hi-IN',
  'id-ID',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'pl-PL',
  'pt-BR',
  'ru-RU',
  'th-TH',
  'tr-TR',
  'vi-VN',
  'zh-CN',
  'zh-TW',
] as const

export type ChromeOnDeviceLanguage = (typeof CHROME_ON_DEVICE_LANGUAGES)[number]

/** Map browser locales to the nearest Chrome on-device language pack. */
const LOCALE_TO_ON_DEVICE_LANG: Record<string, ChromeOnDeviceLanguage> = {
  de: 'de-DE',
  'de-DE': 'de-DE',
  'de-AT': 'de-DE',
  'de-CH': 'de-DE',
  en: 'en-US',
  'en-US': 'en-US',
  'en-GB': 'en-US',
  'en-AU': 'en-US',
  'en-CA': 'en-US',
  'en-IN': 'en-US',
  'en-NZ': 'en-US',
  es: 'es-ES',
  'es-ES': 'es-ES',
  'es-US': 'es-ES',
  'es-MX': 'es-ES',
  'es-419': 'es-ES',
  fr: 'fr-FR',
  'fr-FR': 'fr-FR',
  'fr-CA': 'fr-FR',
  hi: 'hi-IN',
  'hi-IN': 'hi-IN',
  id: 'id-ID',
  'id-ID': 'id-ID',
  it: 'it-IT',
  'it-IT': 'it-IT',
  ja: 'ja-JP',
  'ja-JP': 'ja-JP',
  ko: 'ko-KR',
  'ko-KR': 'ko-KR',
  pl: 'pl-PL',
  'pl-PL': 'pl-PL',
  pt: 'pt-BR',
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt-BR',
  ru: 'ru-RU',
  'ru-RU': 'ru-RU',
  th: 'th-TH',
  'th-TH': 'th-TH',
  tr: 'tr-TR',
  'tr-TR': 'tr-TR',
  vi: 'vi-VN',
  'vi-VN': 'vi-VN',
  zh: 'zh-CN',
  'zh-CN': 'zh-CN',
  'zh-Hans': 'zh-CN',
  'zh-TW': 'zh-TW',
  'zh-Hant': 'zh-TW',
  'zh-HK': 'zh-TW',
}

/**
 * Quality tiers for pack probe/install, best-first for chat dictation.
 * `command` is last — it is for short voice commands; using it while the live
 * session has no quality field can yield mic activity with no transcript.
 */
const ON_DEVICE_QUALITIES: (SpeechRecognitionQuality | undefined)[] = [
  'dictation',
  'conversation',
  undefined,
  'command',
]

const PACK_POLL_INTERVAL_MS = 500
const PACK_INSTALL_MAX_WAIT_MS = 120_000

let cachedConstructor: SpeechRecognitionStatic | undefined

/**
 * Resolve the SpeechRecognition constructor once. The `window` globals never
 * change within a session, so the first successful lookup is memoized.
 */
function getSpeechRecognitionConstructor():
  SpeechRecognitionStatic | undefined {
  if (cachedConstructor) return cachedConstructor
  if (typeof window === 'undefined') return undefined

  const constructors = [
    window.SpeechRecognition,
    window.webkitSpeechRecognition,
  ].filter((Ctor): Ctor is SpeechRecognitionStatic => Boolean(Ctor))

  cachedConstructor =
    constructors.find(
      (Ctor) =>
        typeof Ctor.available === 'function' &&
        typeof Ctor.install === 'function',
    ) ?? constructors[0]

  return cachedConstructor
}

export function normalizeUserLang(
  locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US',
): ChromeOnDeviceLanguage | null {
  const normalized = locale.trim()
  if (!normalized) return null

  const direct = LOCALE_TO_ON_DEVICE_LANG[normalized]
  if (direct) return direct

  const base = normalized.split('-')[0]
  return LOCALE_TO_ON_DEVICE_LANG[base] ?? null
}

export function hasOnDeviceSpeechApi(): boolean {
  const Ctor = getSpeechRecognitionConstructor()
  return Boolean(Ctor?.available && Ctor.install)
}

const BROWSER_DICTATION_REQUIREMENTS_MESSAGE =
  'On-device dictation needs Chrome 139+ on desktop with a supported language. For server transcription locally, set VITE_DICTATION_PROVIDER=deepgram.'

export function getBrowserDictationErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'not-allowed':
      return 'Microphone access was denied. Allow the mic for this site in the browser bar and try again.'
    case 'network':
      return BROWSER_DICTATION_REQUIREMENTS_MESSAGE
    case 'audio-capture':
      return 'No microphone was found, or it is in use by another application.'
    case 'service-not-allowed':
      return 'Speech recognition is blocked by the browser or your organization.'
    case 'language-not-supported':
      return 'This language is not supported for on-device dictation in Chrome.'
    default:
      return `Dictation failed (${errorCode}). Please try again.`
  }
}

function buildOnDeviceRecognitionOptions(
  lang: ChromeOnDeviceLanguage,
  quality?: SpeechRecognitionQuality,
): SpeechRecognitionOptions {
  return {
    langs: [lang],
    processLocally: true,
    ...(quality ? { quality } : {}),
  }
}

export interface OnDeviceProbeResult {
  status: SpeechRecognitionAvailability
  /** The quality tier that responded; `undefined` means the default (no quality) tier. */
  quality?: SpeechRecognitionQuality
}

/**
 * Probe on-device availability for a language, returning the first quality tier
 * that is not `'unavailable'` (and that tier, so the caller can reuse it for install).
 */
export async function probeOnDeviceDictation(
  lang: ChromeOnDeviceLanguage,
): Promise<OnDeviceProbeResult> {
  const Ctor = getSpeechRecognitionConstructor()
  if (!Ctor?.available) return { status: 'unavailable' }

  for (const quality of ON_DEVICE_QUALITIES) {
    const status = await Ctor.available(
      buildOnDeviceRecognitionOptions(lang, quality),
    )
    if (status !== 'unavailable') return { status, quality }
  }

  return { status: 'unavailable' }
}

/** Dev helper: run in the console on the EverGPT tab to inspect pack / API state. */
export async function logBrowserDictationDiagnostics(
  locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US',
): Promise<void> {
  const lang = normalizeUserLang(locale)
  const Ctor = getSpeechRecognitionConstructor()
  console.info('[dictation] diagnose', {
    navigatorLanguage: locale,
    mappedLang: lang,
    hasCtor: Boolean(Ctor),
    hasOnDeviceApi: hasOnDeviceSpeechApi(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  })
  if (!Ctor?.available || !lang) return

  for (const quality of ON_DEVICE_QUALITIES) {
    const status = await Ctor.available(
      buildOnDeviceRecognitionOptions(lang, quality),
    )
    console.info(
      `[dictation] SpeechRecognition.available quality=${
        quality ?? 'default'
      } →`,
      status,
    )
  }
}

/**
 * Ensure the on-device pack for `lang`/`quality` is installed, downloading and
 * polling as needed. `shouldContinue` is checked each iteration so a cancelled
 * session can abort a long download instead of running to the 120s deadline.
 */
export async function ensureOnDeviceLanguagePack(
  lang: ChromeOnDeviceLanguage,
  quality: SpeechRecognitionQuality | undefined,
  onStatus?: (message: string) => void,
  shouldContinue: () => boolean = () => true,
): Promise<'ready' | 'install_failed' | 'unavailable'> {
  const Ctor = getSpeechRecognitionConstructor()
  if (!Ctor?.available || !Ctor.install) return 'unavailable'

  const options = buildOnDeviceRecognitionOptions(lang, quality)
  const deadline = Date.now() + PACK_INSTALL_MAX_WAIT_MS

  while (Date.now() < deadline) {
    if (!shouldContinue()) return 'unavailable'

    const status = await Ctor.available(options)
    if (status === 'available') return 'ready'
    if (status === 'unavailable') return 'unavailable'

    onStatus?.('Downloading on-device speech model…')

    if (status === 'downloadable') {
      if (import.meta.env.DEV) {
        console.info('[dictation] installing on-device pack', { lang, quality })
      }
      const installed = await Ctor.install(options)
      if (!installed) return 'install_failed'
      continue
    }

    // 'downloading' — another trigger is already fetching the pack; poll until ready.
    await new Promise((resolve) => setTimeout(resolve, PACK_POLL_INTERVAL_MS))
  }

  return 'install_failed'
}

export interface DictationTranscriptUpdate {
  finalText: string
  interimText: string
}

export function mergeRecognitionResults(
  results: SpeechRecognitionResultList,
): DictationTranscriptUpdate {
  let finalText = ''
  let interimText = ''

  for (let i = 0; i < results.length; i++) {
    const result = results[i]
    const transcript = result[0]?.transcript ?? ''
    if (!transcript) continue

    if (result.isFinal) {
      finalText += transcript
    } else {
      interimText += transcript
    }
  }

  return { finalText: finalText.trim(), interimText: interimText.trim() }
}

export interface SpeechDictationSessionCallbacks {
  onTranscript: (update: DictationTranscriptUpdate) => void
  onError: (errorCode: string) => void
  onEnd: () => void
}

export interface SpeechDictationSession {
  start: () => void
  stop: () => void
  abort: () => void
}

export interface SpeechDictationSessionOptions {
  /** When false, omits `processLocally` (e.g. dev without Chrome on-device packs). */
  processLocally?: boolean
}

export function createSpeechDictationSession(
  lang: ChromeOnDeviceLanguage,
  callbacks: SpeechDictationSessionCallbacks,
  options: SpeechDictationSessionOptions = {},
): SpeechDictationSession | null {
  const Ctor = getSpeechRecognitionConstructor()
  if (!Ctor) return null

  const recognition = new Ctor()
  recognition.continuous = true
  recognition.interimResults = true
  recognition.maxAlternatives = 1
  recognition.lang = lang
  if (options.processLocally !== false) {
    recognition.processLocally = true
  }

  recognition.onresult = (event: SpeechRecognitionEvent) => {
    callbacks.onTranscript(mergeRecognitionResults(event.results))
  }

  recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
    if (event.error === 'aborted' || event.error === 'no-speech') return
    callbacks.onError(event.error)
  }

  recognition.onend = () => {
    callbacks.onEnd()
  }

  return {
    start: () => recognition.start(),
    stop: () => recognition.stop(),
    abort: () => recognition.abort(),
  }
}

/** Speak during the window; logs interim/final text and errors to the console. */
export async function runDictationLiveTest(
  options: { locale?: string; seconds?: number } = {},
): Promise<void> {
  const locale =
    options.locale ??
    (typeof navigator !== 'undefined' ? navigator.language : 'en-US')
  const seconds = options.seconds ?? 8
  const lang = normalizeUserLang(locale)
  if (!lang) {
    console.error('[dictation] live-test: unsupported language', locale)
    return
  }

  const onDevice = await probeOnDeviceDictation(lang)
  console.info('[dictation] live-test: starting', {
    locale,
    lang,
    seconds,
    onDevice,
  })

  let heardAny = false
  const session = createSpeechDictationSession(lang, {
    onTranscript: (update) => {
      if (update.finalText || update.interimText) heardAny = true
      console.info('[dictation] live-test: transcript', update)
    },
    onError: (errorCode) => {
      console.error('[dictation] live-test: error', errorCode)
    },
    onEnd: () => {
      console.info('[dictation] live-test: recognition ended')
    },
  })

  if (!session) {
    console.error('[dictation] live-test: SpeechRecognition not available')
    return
  }

  try {
    session.start()
  } catch (error) {
    console.error('[dictation] live-test: start() threw', error)
    return
  }

  await new Promise((resolve) => setTimeout(resolve, seconds * 1000))
  session.stop()
  console.info('[dictation] live-test: finished', {
    heardAny,
    hint: heardAny
      ? 'Engine produced text — if the UI stays empty, check the Vue recorder.'
      : 'No text events — check mic, language pack (diagnose), and Chrome 139+ desktop.',
  })
}

export type EvergptDictationConsoleApi = {
  /** Pack + API availability for the user locale. */
  diagnose: typeof logBrowserDictationDiagnostics
  /** On-device probe only (no mic session). */
  probe: (
    locale?: string,
  ) => Promise<
    | { lang: null; error: 'unsupported' }
    | ({ lang: ChromeOnDeviceLanguage } & OnDeviceProbeResult)
  >
  /** Short listening session with console logging. */
  liveTest: typeof runDictationLiveTest
}

/** Attach `evergptDictation.*` on `window` for QA / support in the browser console. */
export function registerDictationConsoleHelpers(): void {
  if (typeof window === 'undefined') return

  const api: EvergptDictationConsoleApi = {
    diagnose: logBrowserDictationDiagnostics,
    probe: async (locale = navigator.language) => {
      const lang = normalizeUserLang(locale)
      if (!lang) return { lang: null, error: 'unsupported' as const }
      const result = await probeOnDeviceDictation(lang)
      return { lang, ...result }
    },
    liveTest: runDictationLiveTest,
  }

  ;(
    window as Window & { evergptDictation?: EvergptDictationConsoleApi }
  ).evergptDictation = api

  if (import.meta.env.DEV) {
    console.info(
      '[dictation] QA helpers on window: evergptDictation.diagnose(), .probe(), .liveTest()',
    )
  }
}
